// src/components/programs/ProgramForm/Step2Sections/DayQuotation/ProductQuotationCard.jsx

import React, { useState, useMemo } from "react";
import {
    Card,
    CardContent,
    Box,
    Typography,
    IconButton,
    Chip,
    Grid,
    Collapse,
    Divider,
    Switch,
    FormControlLabel,
    Stack,
    alpha,
    useTheme,
    Badge,
} from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon,
    // AttachMoney as PriceIcon,
    // MoneyOff as CostIcon,
    Edit as EditIcon,
    Discount as DiscountIcon,
    Store as SupplierIcon,
    TrendingUp as ProfitIcon,
    ConfirmationNumber as TicketIcon,
} from "@mui/icons-material";
import FormInput from "../../../../inputs/FormInput";
import { calculateProductTotals, formatPrice, formatNumber as formatNum, getProductTypeGroup } from "../../helpers/quotationCalculations";
import { PRICING_CODES, PRICING_CODE_LABELS } from "../../helpers/formConstants";

const ProductQuotationCard = ({
    product,
    productIndex,
    onUpdatePricing,
    errors,
    isArabic,
    activeLang,
    paxTypes,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [isExpanded, setIsExpanded] = useState(false);

    // Calculate product totals
    const productTotals = useMemo(() => {
        const calculated = calculateProductTotals(product, paxTypes);
        return {
            ...calculated,
            total_profit: calculated.total_price - calculated.total_cost,
        };
    }, [product, paxTypes]);

    // Product info
    const title = product.product_snapshot?.title?.[activeLang] ||
        product.product_snapshot?.title?.en ||
        'N/A';

    const pricingUnit = product.product_snapshot?.pricing_unit || product.pricing_unit || product.pricing?.pricing_unit || '';

    // Pricing info
    const pricingInfo = useMemo(() => {
        const defaultIcon = '📋';
        const defaultColor = 'primary';
        const labelObj = PRICING_CODE_LABELS[pricingUnit];
        const labelStr = labelObj ? (isArabic ? labelObj.ar : labelObj.en) : pricingUnit;

        if (pricingUnit === PRICING_CODES.PER_PERSON || pricingUnit === PRICING_CODES.PER_PERSON_NIGHT) {
            return {
                label: labelStr,
                unitLabel: isArabic ? 'شخص' : 'Person',
                icon: '👤',
                color: 'info'
            };
        } else if (pricingUnit === PRICING_CODES.PER_NIGHT || pricingUnit === PRICING_CODES.ROOM_NIGHT) {
            return {
                label: labelStr,
                unitLabel: isArabic ? 'ليلة' : 'Night',
                icon: '🌙',
                color: 'info'
            };
        } else if (pricingUnit === PRICING_CODES.PER_UNIT) {
            return {
                label: labelStr,
                unitLabel: isArabic ? 'وحدة' : 'Unit',
                icon: '📦',
                color: 'success'
            };
        }
        return {
            label: labelStr,
            unitLabel: 'Unit',
            icon: defaultIcon,
            color: defaultColor
        };
    }, [pricingUnit, isArabic]);

    const activePaxTypes = useMemo(() => {
        return Array.isArray(paxTypes)
            ? paxTypes.filter((pt) => Number(pt.count) > 0)
            : [];
    }, [paxTypes]);

    const perPersonBreakdownTotals = useMemo(() => {
        const paxPrices = product.product_snapshot?.pax_type_prices || product.pax_type_prices || [];

        return activePaxTypes.reduce((acc, pt) => {
            const count = Number(pt.count) || 0;
            const paxConfig = paxPrices.find((p) => String(p.pax_type_id) === String(pt.pax_type_id)) || {};

            const customPaxPrice = parseFloat(product.pricing?.custom_pax_prices?.[pt.pax_type_id]);
            const hasCustomPaxPrice = product.pricing?.is_price_edited && !isNaN(customPaxPrice) && customPaxPrice >= 0;
            const pricePerPax = hasCustomPaxPrice ? customPaxPrice : (parseFloat(paxConfig.price) || 0);

            const customCost = product.pricing?.custom_pax_costs?.[pt.pax_type_id];
            const hasCustomCost = customCost !== undefined && customCost !== null && !isNaN(parseFloat(customCost));
            const costPerPax = hasCustomCost ? parseFloat(customCost) : (parseFloat(paxConfig.cost_price) || 0);

            const typeGroup = getProductTypeGroup(product);
            const isFlight = typeGroup === 'flight';
            const effectiveRepetitions = isFlight ? 1 : (product.repetition_count || 1);

            return {
                sellTotal: acc.sellTotal + (pricePerPax * count * effectiveRepetitions),
                costTotal: acc.costTotal + (costPerPax * count * effectiveRepetitions),
            };
        }, { sellTotal: 0, costTotal: 0 });
    }, [activePaxTypes, product]);

    // Supplier and Service
    const supplier = product.selected_supplier;

    // Profit margin
    const profitMargin = productTotals.total_price > 0
        ? ((productTotals.total_profit / productTotals.total_price) * 100).toFixed(1)
        : 0;

    // Discount type options
    const discountTypeOptions = [
        { id: "zero", name: isArabic ? "بدون خصم" : "No Discount" },
        { id: "amount", name: isArabic ? "مبلغ ثابت" : "Fixed Amount" },
        { id: "percent", name: isArabic ? "نسبة مئوية" : "Percentage" },
    ];

    // Determine if itinerary note should be shown
    const showItineraryNote = pricingUnit === 'per_person' || pricingUnit === 'per_unit';

    // Original prices
    const originalSellPrice = product.pricing?.original_sell_price ||
        product.product_snapshot?.original_sell_price ||
        product.product_snapshot?.original_price ||
        0;

    const originalCostPrice = product.pricing?.cost_price ||
        product.product_snapshot?.original_cost_price ||
        0;

    // Handle custom price toggle
    const handleCustomPriceToggle = (checked) => {
        if (checked) {
            // ✅ Only set global custom price if NOT per-person
            if (pricingUnit !== 'per_person' && pricingUnit !== 'per_person_night') {
                onUpdatePricing(product.locations, 'pricing.custom_price', product.pricing?.price || 0);
                onUpdatePricing(product.locations, 'pricing.custom_cost_price',
                    product.pricing?.cost_price != null
                        ? product.pricing.cost_price
                        : product.pricing?.original_cost_price ?? 0
                );
            }
            onUpdatePricing(product.locations, 'pricing.is_price_edited', true);
        } else {
            onUpdatePricing(product.locations, 'pricing.custom_price', null);
            onUpdatePricing(product.locations, 'pricing.is_price_edited', false);
            // ✅ Reset custom cost values when manual pricing is disabled
            onUpdatePricing(product.locations, 'pricing.custom_pax_costs', {});
            onUpdatePricing(product.locations, 'pricing.custom_cost_price', null);
        }
    };

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1.5px solid ${alpha(isDark ? '#FFFFFF' : '#000000', 0.08)}`,
                overflow: 'hidden',
                transition: 'all 0.2s',
                '&:hover': {
                    boxShadow: 2,
                    borderColor: theme.palette.primary.main,
                }
            }}
        >
            {/* Compact Top Strip */}
            <Box
                sx={{
                    height: 4,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
            />

            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                {/* Compact Header */}
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
                    {/* Product Number */}
                    <Badge
                        badgeContent={productTotals.quantity > 0 ? `×${formatNum(productTotals.quantity, 0)}` : null}
                        color="secondary"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                height: 18,
                                minWidth: 18,
                            }
                        }}
                    >
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1.5,
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: '1rem',
                            }}
                        >
                            {product.order}
                        </Box>
                    </Badge>

                    {/* Product Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{
                                fontSize: '1rem',
                                mb: 0.75,
                                lineHeight: 1.3,
                            }}
                        >
                            {title}
                        </Typography>

                        {/* Compact Tags */}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {/* Pricing Type */}
                            <Chip
                                label={`${pricingInfo.icon} ${pricingInfo.label}`}
                                size="small"
                                sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    bgcolor: alpha(theme.palette[pricingInfo.color].main, 0.08),
                                    color: `${pricingInfo.color}.main`,
                                    border: `1px solid ${alpha(theme.palette[pricingInfo.color].main, 0.15)}`,
                                }}
                            />

                            {/* Repetition Count */}
                            {product.repetition_count > 1 && (
                                <Chip
                                    icon={<TicketIcon style={{ fontSize: 16 }} />}
                                    label={`${product.repetition_count}×`}
                                    size="small"
                                    sx={{
                                        height: 24,
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        bgcolor: alpha(theme.palette.info.main, 0.08),
                                        color: 'info.main',
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                                    }}
                                />
                            )}

                            {/* Supplier */}
                            {supplier && (
                                <Chip
                                    icon={<SupplierIcon style={{ fontSize: 16 }} />}
                                    label={supplier.name?.[activeLang] || supplier.name?.en || 'N/A'}
                                    size="small"
                                    sx={{
                                        height: 24,
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        maxWidth: 150,
                                        '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }
                                    }}
                                />
                            )}

                            {/* Discount Badge */}
                            {product.pricing?.discount_type !== 'zero' && (
                                <Chip
                                    icon={<DiscountIcon style={{ fontSize: 16 }} />}
                                    label={isArabic ? 'خصم' : 'Discount'}
                                    size="small"
                                    color="warning"
                                    sx={{
                                        height: 24,
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                    }}
                                />
                            )}

                            {/* Edited Badge */}
                            {!product.pricing?.is_price_edited || (
                                <Chip
                                    icon={<EditIcon style={{ fontSize: 16 }} />}
                                    label={isArabic ? 'معدل' : 'Edited'}
                                    size="small"
                                    color="error"
                                    sx={{
                                        height: 24,
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                    }}
                                />
                            )}
                        </Stack>

                        {/* Itinerary Note Display */}
                        {showItineraryNote && product.itinerary_note?.[activeLang] && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1, borderLeft: `3px solid ${theme.palette.info.main}` }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                                    {product.itinerary_note[activeLang]}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Expand Button */}
                    <IconButton
                        size="small"
                        onClick={() => setIsExpanded(!isExpanded)}
                        sx={{
                            width: 28,
                            height: 28,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                            },
                        }}
                    >
                        <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                </Stack>

                {/* Compact Financial Summary - 3 Columns */}
                <Grid container spacing={1.5}>
                    <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
                                {/* <PriceIcon sx={{ fontSize: 14, color: 'success.main' }} /> */}
                                <Typography
                                    color="success.main"
                                    display="block">
                                    ฿
                                </Typography>
                                <Typography variant="caption" color="success.main" fontWeight={600} sx={{ fontSize: '1rem' }}>
                                    {isArabic ? 'سعر' : 'Price'}
                                </Typography>
                            </Stack>
                            <Typography variant="h6" fontWeight={800} color="success.main" sx={{ fontSize: '1rem' }}>
                                {formatPrice(productTotals.total_price)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha(theme.palette.error.main, 0.05), borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
                                {/* <CostIcon sx={{ fontSize: 14, color: 'error.main' }} /> */}
                                <Typography
                                    color="error.main"
                                    display="block">
                                    ฿
                                </Typography>
                                <Typography variant="caption" color="error.main" fontWeight={600} sx={{ fontSize: '1rem' }}>
                                    {isArabic ? 'تكلفة' : 'Cost'}
                                </Typography>
                            </Stack>
                            <Typography variant="h6" fontWeight={800} color="error.main" sx={{ fontSize: '1rem' }}>
                                {formatPrice(productTotals.total_cost)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ mb: 0.5 }}>
                                <ProfitIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="caption" color="primary.main" fontWeight={600} sx={{ fontSize: '1rem' }}>
                                    {isArabic ? 'ربح' : 'Profit'}
                                </Typography>
                            </Stack>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                    <Chip
                                        label={`${profitMargin}%`}
                                        size="small"
                                        color={parseFloat(profitMargin) > 0 ? 'success' : 'error'}
                                        sx={{ fontWeight: 700, fontSize: '1rem', height: 20 }}
                                    />
                                </Stack>
                                <Typography variant="caption" display="block" color={productTotals.total_profit < 0 ? "error.main" : "text.secondary"} sx={{ mt: 0.3, fontSize: '1rem' }}>
                                    {formatPrice(productTotals.total_profit)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                {/* Collapsible Edit Section */}
                <Collapse in={isExpanded} timeout="auto">
                    <Divider sx={{ my: 1.5 }} />

                    <Grid container spacing={1.5}>
                        {/* ✅ Calculation Formulas — per-pax-type breakdown for per_person */}
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '1rem', mb: 1, display: 'block' }}>
                                📐 {isArabic ? 'العمليات الحسابية' : 'Calculations'}
                            </Typography>

                            {(pricingUnit === 'per_person' || pricingUnit === 'per_person_night') && (product.product_snapshot?.pax_type_prices?.length > 0 || product.pax_type_prices?.length > 0) ? (
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={6}>
                                        {/* Cost Price Breakdown */}
                                        <Box sx={{ p: 1, bgcolor: alpha(theme.palette.error.main, 0.03), borderRadius: 1, border: `1px solid ${alpha(theme.palette.error.main, 0.15)}` }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.78rem', mb: 1, fontWeight: 700 }}>
                                                {isArabic ? '📊 سعر التكلفة:' : '📊 Cost Price:'}
                                            </Typography>
                                            <Stack spacing={0.5}>
                                                {activePaxTypes.map((pt) => {
                                                    const paxPrices = product.product_snapshot?.pax_type_prices || product.pax_type_prices || [];
                                                    const paxConfig = paxPrices.find(p => String(p.pax_type_id) === String(pt.pax_type_id));

                                                    // ✅ Skip if no pricing found for this pax type in the snapshot (prevent potential issues)
                                                    // if (!paxConfig) return null;

                                                    const customCost = product.pricing?.custom_pax_costs?.[pt.pax_type_id];
                                                    const hasCustomCost = customCost !== undefined && customCost !== null && !isNaN(parseFloat(customCost));
                                                    const costPerPax = hasCustomCost
                                                        ? parseFloat(customCost)
                                                        : (parseFloat(paxConfig?.cost_price) || 0);

                                                    const typeGroup = getProductTypeGroup(product);
                                                    const isFlight = typeGroup === 'flight';
                                                    const effectiveRepetitions = isFlight ? 1 : (product.repetition_count || 1);

                                                    const subtotal = costPerPax * (pt.count || 0) * effectiveRepetitions;
                                                    const paxName = pt.pax_type_name?.[activeLang] || pt.pax_type_name?.en || pt.pax_type_name || `Pax ${pt.pax_type_id}`;
                                                    return (
                                                        <Stack key={pt.pax_type_id} direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                                            <Chip label={paxName} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main' }} />
                                                            <Typography variant="caption" fontWeight={700} color="text.secondary">:</Typography>
                                                            <Chip
                                                                label={formatPrice(costPerPax)}
                                                                size="small"
                                                                sx={{
                                                                    height: 20, fontSize: '0.8rem', fontWeight: 600,
                                                                    bgcolor: hasCustomCost ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.error.main, 0.1),
                                                                    color: hasCustomCost ? 'warning.dark' : 'inherit',
                                                                    border: hasCustomCost ? `1px solid ${alpha(theme.palette.warning.main, 0.4)}` : 'none',
                                                                }}
                                                            />
                                                            <Typography variant="caption" fontWeight={700}>×</Typography>
                                                            <Chip label={`${pt.count}`} size="small" sx={{ height: 20, fontSize: '0.8rem', fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                                                            {effectiveRepetitions > 1 && (
                                                                <>
                                                                    <Typography variant="caption" fontWeight={700}>×</Typography>
                                                                    <Chip label={`${effectiveRepetitions} ${isArabic ? 'مرات' : 'times'}`} size="small" sx={{ height: 20, fontSize: '0.8rem', fontWeight: 600, bgcolor: alpha(theme.palette.warning.main, 0.1) }} />
                                                                </>
                                                            )}
                                                            <Typography variant="caption" fontWeight={700}>=</Typography>
                                                            <Chip label={formatPrice(subtotal)} size="small" color="error" sx={{ height: 20, fontSize: '0.8rem', fontWeight: 700 }} />
                                                        </Stack>
                                                    );
                                                })}
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5, borderTop: `1px dashed ${alpha(theme.palette.error.main, 0.3)}` }}>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Typography variant="caption" fontWeight={700} color="text.secondary">{isArabic ? 'الإجمالي:' : 'Total:'}</Typography>
                                                        <Chip label={formatPrice(perPersonBreakdownTotals.costTotal)} size="small" color="error" sx={{ fontWeight: 800, fontSize: '0.9rem', height: 24 }} />
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        {/* Sell Price Breakdown */}
                                        <Box sx={{ p: 1, bgcolor: alpha(theme.palette.success.main, 0.03), borderRadius: 1, border: `1px solid ${alpha(theme.palette.success.main, 0.15)}` }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.78rem', mb: 1, fontWeight: 700 }}>
                                                {isArabic ? '💵 سعر البيع:' : '💵 Sell Price:'}
                                            </Typography>
                                            <Stack spacing={0.5}>
                                                {activePaxTypes.map((pt) => {
                                                    const paxPrices = product.product_snapshot?.pax_type_prices || product.pax_type_prices || [];
                                                    const paxConfig = paxPrices.find(p => String(p.pax_type_id) === String(pt.pax_type_id));

                                                    // ✅ Skip if no pricing found for this pax type in the snapshot (prevent potential issues)
                                                    // if (!paxConfig) return null;

                                                    const customPaxPrice = parseFloat(product.pricing?.custom_pax_prices?.[pt.pax_type_id]);
                                                    const hasCustomPaxPrice = !isNaN(customPaxPrice) && customPaxPrice >= 0 && product.pricing?.is_price_edited;

                                                    const pricePerPax = hasCustomPaxPrice
                                                        ? customPaxPrice
                                                        : (parseFloat(paxConfig?.price) || 0);

                                                    const typeGroup = getProductTypeGroup(product);
                                                    const isFlight = typeGroup === 'flight';
                                                    const effectiveRepetitions = isFlight ? 1 : (product.repetition_count || 1);

                                                    const subtotal = pricePerPax * (pt.count || 0) * effectiveRepetitions;
                                                    const paxName = pt.pax_type_name?.[activeLang] || pt.pax_type_name?.en || pt.pax_type_name || `Pax ${pt.pax_type_id}`;
                                                    return (
                                                        <Stack key={pt.pax_type_id} direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                                            <Chip label={paxName} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main' }} />
                                                            <Typography variant="caption" fontWeight={700} color="text.secondary">:</Typography>
                                                            <Chip label={formatPrice(pricePerPax)} size="small" sx={{ height: 20, fontSize: '0.8rem', fontWeight: 600, bgcolor: alpha(theme.palette.success.main, 0.1) }} />
                                                            <Typography variant="caption" fontWeight={700}>×</Typography>
                                                            <Chip label={`${pt.count}`} size="small" sx={{ height: 20, fontSize: '0.8rem', fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                                                            {effectiveRepetitions > 1 && (
                                                                <>
                                                                    <Typography variant="caption" fontWeight={700}>×</Typography>
                                                                    <Chip label={`${effectiveRepetitions} ${isArabic ? 'مرات' : 'times'}`} size="small" sx={{ height: 20, fontSize: '0.8rem', fontWeight: 600, bgcolor: alpha(theme.palette.warning.main, 0.1) }} />
                                                                </>
                                                            )}
                                                            <Typography variant="caption" fontWeight={700}>=</Typography>
                                                            <Chip label={formatPrice(subtotal)} size="small" color="success" sx={{ height: 20, fontSize: '0.8rem', fontWeight: 700 }} />
                                                        </Stack>
                                                    );
                                                })}
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5, borderTop: `1px dashed ${alpha(theme.palette.success.main, 0.3)}` }}>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Typography variant="caption" fontWeight={700} color="text.secondary">{isArabic ? 'الإجمالي:' : 'Total:'}</Typography>
                                                        <Chip label={formatPrice(perPersonBreakdownTotals.sellTotal)} size="small" color="success" sx={{ fontWeight: 800, fontSize: '0.9rem', height: 24 }} />
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                </Grid>
                            ) : (
                                /* === Legacy: single price × quantity === */
                                <Grid container spacing={1}>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ p: 1, bgcolor: alpha(theme.palette.error.main, 0.03), borderRadius: 1, border: `1px solid ${alpha(theme.palette.error.main, 0.15)}` }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.78rem', mb: 0.5, fontWeight: 700 }}>
                                                {isArabic ? 'سعر التكلفة:' : 'Cost Price:'}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                                <Chip label={formatPrice(productTotals.final_cost_per_unit)} size="small" sx={{ height: 20, fontSize: '1rem', fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                                                <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>×</Typography>
                                                <Chip label={`${formatNum(productTotals.quantity, 0)} ${pricingInfo.unitLabel}`} size="small" sx={{ height: 20, fontSize: '1rem', fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                                                <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>=</Typography>
                                                <Chip label={formatPrice(productTotals.total_cost)} size="small" color="error" sx={{ height: 20, fontSize: '1rem', fontWeight: 700 }} />
                                            </Stack>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ p: 1, mb: 0, bgcolor: alpha(theme.palette.success.main, 0.03), borderRadius: 1, border: `1px solid ${alpha(theme.palette.success.main, 0.15)}` }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.78rem', mb: 0.5, fontWeight: 700 }}>
                                                {isArabic ? 'سعر البيع:' : 'Sell Price:'}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                                <Chip label={formatPrice(productTotals.final_price_per_unit)} size="small" sx={{ height: 20, fontSize: '1rem', fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                                                <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>×</Typography>
                                                <Chip label={`${formatNum(productTotals.quantity, 0)} ${pricingInfo.unitLabel}`} size="small" sx={{ height: 20, fontSize: '1rem', fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
                                                <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>=</Typography>
                                                <Chip label={formatPrice(productTotals.total_price)} size="small" color="success" sx={{ height: 20, fontSize: '1rem', fontWeight: 700 }} />
                                            </Stack>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                        </Grid>

                        {/* Custom Price Toggle */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={product.pricing?.is_price_edited || false}
                                        onChange={(e) => handleCustomPriceToggle(e.target.checked)}
                                        color="warning"
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                                        {isArabic ? '✏️ تعديل السعر يدوياً' : '✏️ Edit Price Manually'}
                                    </Typography>
                                }
                            />
                        </Grid>

                        {/* Custom Price & Cost Inputs */}
                        {!product.pricing?.is_price_edited || (
                            <Grid item xs={12}>
                                {(pricingUnit === 'per_person' || pricingUnit === 'per_person_night') && (product.product_snapshot?.pax_type_prices?.length > 0 || product.pax_type_prices?.length > 0) ? (
                                    // ✅ Per-person: عرض كل نوع راكب في 6 أعمدة بحيث يمكن أن يجلس Type بجانب Type آخر
                                    <Grid container spacing={1.5}>
                                        {activePaxTypes.map((pt) => {
                                            const paxPrices = product.product_snapshot?.pax_type_prices || product.pax_type_prices || [];
                                            const ptp = paxPrices.find(p => String(p.pax_type_id) === String(pt.pax_type_id));

                                            // ✅ Skip if no pricing found for this pax type in the snapshot (temporary fix)
                                            if (!ptp) return null;

                                            const paxName = pt.pax_type_name?.[activeLang] || pt.pax_type_name?.en || pt.pax_type_name || 'Pax';

                                            const customPaxPriceValue = product.pricing?.custom_pax_prices?.hasOwnProperty(pt.pax_type_id)
                                                ? product.pricing.custom_pax_prices[pt.pax_type_id]
                                                : (product.pricing?.is_price_edited ? (ptp?.price != null ? parseFloat(ptp.price) : '') : '');

                                            const customPaxCostValue = product.pricing?.custom_pax_costs?.hasOwnProperty(pt.pax_type_id)
                                                ? product.pricing.custom_pax_costs[pt.pax_type_id]
                                                : (product.pricing?.is_price_edited ? (ptp?.cost_price != null ? parseFloat(ptp.cost_price) : '') : '');

                                            return (
                                                <Grid item xs={12} md={6} key={pt.pax_type_id}>
                                                    <Box
                                                        sx={{
                                                            p: 1,
                                                            borderRadius: 1,
                                                            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                                            bgcolor: alpha(theme.palette.background.paper, 0.5),
                                                        }}
                                                    >
                                                        {/* Pax Type Label */}
                                                        <Typography
                                                            variant="caption"
                                                            fontWeight={700}
                                                            color="text.secondary"
                                                            sx={{ fontSize: '0.7rem', display: 'block', mb: 0.75 }}
                                                        >
                                                            👤 {paxName} ({pt.count})
                                                        </Typography>
                                                        <Grid container spacing={1}>
                                                            {/* ✅ Cost Price Input */}
                                                            <Grid item xs={6}>
                                                                <FormInput
                                                                    label={isArabic ? '📊 تكلفة' : '📊 Cost'}
                                                                    type="number"
                                                                    name={`${product.instance_id}_custom_cost_${pt.pax_type_id}`}
                                                                    value={customPaxCostValue ?? ''}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        onUpdatePricing(
                                                                            product.locations,
                                                                            `pricing.custom_pax_costs.${pt.pax_type_id}`,
                                                                            val === '' ? '' : val
                                                                        );
                                                                    }}
                                                                    inputProps={{ min: 0, step: 0.01 }}
                                                                    size="small"
                                                                    sx={{ width: '100%' }}
                                                                />
                                                            </Grid>
                                                            {/* Sell Price Input */}
                                                            <Grid item xs={6}>
                                                                <FormInput
                                                                    label={isArabic ? '💵 بيع' : '💵 Sell'}
                                                                    type="number"
                                                                    name={`${product.instance_id}_custom_price_${pt.pax_type_id}`}
                                                                    value={customPaxPriceValue ?? ''}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        onUpdatePricing(
                                                                            product.locations,
                                                                            `pricing.custom_pax_prices.${pt.pax_type_id}`,
                                                                            val === '' ? '' : val
                                                                        );
                                                                    }}
                                                                    inputProps={{ min: 0, step: 0.01 }}
                                                                    size="small"
                                                                    sx={{ width: '100%' }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                ) : (
                                    // Non per-person: sell + cost في حقلين بجانب بعض
                                    <Grid container spacing={1.5}>
                                        {/* ✅ NEW: Custom Cost for non-per-person */}
                                        <Grid item xs={6} sm={4} md={3}>
                                            <FormInput
                                                label={isArabic ? '📊 تكلفة مخصصة' : '📊 Custom Cost'}
                                                type="number"
                                                name={`${product.instance_id}_custom_cost_price`}
                                                value={product.pricing?.custom_cost_price != null
                                                    ? (product.pricing.custom_cost_price === '' ? '' : parseFloat(product.pricing.custom_cost_price))
                                                    : (product.pricing?.is_price_edited && product.pricing?.cost_price != null ? parseFloat(product.pricing.cost_price) : '')
                                                }
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    onUpdatePricing(
                                                        product.locations,
                                                        'pricing.custom_cost_price',
                                                        val === '' ? '' : val
                                                    );
                                                }}
                                                inputProps={{ min: 0, step: 0.01 }}
                                                size="small"
                                                sx={{ width: '100%' }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm={4} md={3}>
                                            <FormInput
                                                label={isArabic ? '💵 بيع مخصص' : '💵 Custom Sell'}
                                                type="number"
                                                name={`${product.instance_id}_custom_price`}
                                                value={product.pricing?.custom_price != null ? (product.pricing.custom_price === '' ? '' : parseFloat(product.pricing.custom_price)) : ''}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    onUpdatePricing(
                                                        product.locations,
                                                        'pricing.custom_price',
                                                        val === '' ? '' : val
                                                    );
                                                }}
                                                inputProps={{ min: 0, step: 0.01 }}
                                                size="small"
                                                sx={{ width: '100%' }}
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        )}

                        {/* Itinerary Note */}
                        {showItineraryNote && (
                            <Grid item xs={12}>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={6}>
                                        <FormInput
                                            label={isArabic ? 'ملاحظة (عربي)' : 'Note (Arabic)'}
                                            type="text"
                                            name={`${product.instance_id}_itinerary_note_ar`}
                                            value={product.itinerary_note?.ar || ''}
                                            onChange={(e) =>
                                                onUpdatePricing(
                                                    product.locations,
                                                    'itinerary_note.ar',
                                                    e.target.value
                                                )
                                            }
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormInput
                                            label={isArabic ? 'ملاحظة (إنجليزي)' : 'Note (English)'}
                                            type="text"
                                            name={`${product.instance_id}_itinerary_note_en`}
                                            value={product.itinerary_note?.en || ''}
                                            onChange={(e) =>
                                                onUpdatePricing(
                                                    product.locations,
                                                    'itinerary_note.en',
                                                    e.target.value
                                                )
                                            }
                                            size="small"
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}

                        {/* Discount Type */}
                        <Grid item xs={12} sm={6}>
                            <FormInput
                                label={isArabic ? 'نوع الخصم' : 'Discount Type'}
                                type="select"
                                name={`${product.instance_id}_discount_type`}
                                value={product.pricing?.discount_type || 'zero'}
                                onChange={(e) => {
                                    return onUpdatePricing(
                                        product.locations,
                                        'pricing.discount_type',
                                        e.target.value
                                    );
                                }
                                }
                                options={discountTypeOptions}
                                size="small"
                            />
                        </Grid>

                        {/* Discount Value */}
                        {product.pricing?.discount_type !== 'zero' && (
                            <Grid item xs={12} sm={6}>
                                <FormInput
                                    label={
                                        isArabic
                                            ? product.pricing?.discount_type === 'percent'
                                                ? 'قيمة الخصم (%)'
                                                : 'قيمة الخصم'
                                            : product.pricing?.discount_type === 'percent'
                                                ? 'Discount Value (%)'
                                                : 'Discount Value'
                                    }
                                    type="number"
                                    name={`${product.instance_id}_discount_value`}
                                    value={product.pricing?.discount_value || ''}
                                    onChange={(e) =>
                                        onUpdatePricing(
                                            product.locations,
                                            'pricing.discount_value',
                                            parseFloat(e.target.value) || 0
                                        )
                                    }
                                    error={!!errors[`${product.instance_id}_discount`]}
                                    helperText={errors[`${product.instance_id}_discount`]}
                                    inputProps={{
                                        min: 0,
                                        max: product.pricing?.discount_type === 'percent' ? 100 : undefined,
                                        step: product.pricing?.discount_type === 'percent' ? 1 : 0.01,
                                    }}
                                    size="small"
                                />
                            </Grid>
                        )}

                        {/* Original Prices - Compact */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }}>
                                <Chip label={isArabic ? 'الأسعار الأصلية' : 'Original Prices'} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                            </Divider>
                        </Grid>

                        {(pricingUnit === 'per_person' || pricingUnit === 'per_person_night') && (product.product_snapshot?.pax_type_prices?.length > 0 || product.pax_type_prices?.length > 0) ? (
                            <Grid item xs={12}>
                                <Stack spacing={1}>

                                    {activePaxTypes.map((pt, i) => {
                                        const paxPrices = product.product_snapshot?.pax_type_prices || product.pax_type_prices || [];
                                        const ptp = paxPrices.find(p => String(p.pax_type_id) === String(pt.pax_type_id));
                                        if (!ptp) return null;
                                        return (
                                            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: alpha(theme.palette.divider, 0.04), borderRadius: 1 }}>
                                                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                                    {ptp.pax_type_name?.[activeLang] || ptp.pax_type_name?.en || ptp.pax_type_name || 'Pax'}
                                                </Typography>
                                                <Stack direction="row" spacing={2}>
                                                    <Typography variant="body2" color="success.main" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                                                        {isArabic ? 'بيع:' : 'Sell:'} {formatPrice(ptp.price)}
                                                    </Typography>
                                                    <Typography variant="body2" color="error.main" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                                                        {isArabic ? 'تكلفة:' : 'Cost:'} {formatPrice(ptp.cost_price)}
                                                    </Typography>
                                                </Stack>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={6}>
                                    <Box sx={{ p: 1, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.success.main, 0.15)}` }}>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', mb: 0.5 }}>
                                            {isArabic ? 'سعر البيع' : 'Sell Price'}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: '0.85rem' }}>
                                            {formatPrice(originalSellPrice)}
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={6}>
                                    <Box sx={{ p: 1, bgcolor: alpha(theme.palette.error.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.error.main, 0.15)}` }}>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', mb: 0.5 }}>
                                            {isArabic ? 'سعر التكلفة' : 'Cost Price'}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700} color="error.main" sx={{ fontSize: '0.85rem' }}>
                                            {formatPrice(originalCostPrice)}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Collapse>
            </CardContent>
        </Card>
    );
};

export default ProductQuotationCard;


